import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layout } from '@/components/Layout';
import { useStorage } from '@/hooks/useStorage';
import { speakWord } from '@/lib/index';
import type { WordEntry } from '@/lib/index';
import { PointsBar, StarBurst } from '@/components/HeroReward';

const masteryButtons = [
  { level: 2 as const, emoji: '✨', label: '熟练', points: 5, bg: 'bg-mastery-master', text: 'text-white', border: 'border-mastery-master-border' },
  { level: 1 as const, emoji: '📖', label: '练习', points: 3, bg: 'bg-mastery-familiar', text: 'text-white', border: 'border-mastery-familiar-border' },
  { level: 0 as const, emoji: '❓', label: '加油', points: 0, bg: 'bg-mastery-learning', text: 'text-white', border: 'border-mastery-learning-border' },
];

export default function Review() {
  const { getWordsForReview, updateWordProgress, addPoints } = useStorage();
  const [dueWords, setDueWords] = useState<WordEntry[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showReward, setShowReward] = useState(false);
  const [sessionStats, setSessionStats] = useState({ totalPoints: 0, reviewedCount: 0 });
  const [langRatings, setLangRatings] = useState<{ zh?: 0 | 1 | 2; jp?: 0 | 1 | 2; en?: 0 | 1 | 2 }>({});

  useEffect(() => {
    const words = getWordsForReview();
    setDueWords(words);
    if (words.length === 0) {
      setShowSummary(true);
    }
  }, [getWordsForReview]);

  const currentWord = dueWords[currentIndex];
  const totalWords = dueWords.length;

  const handleFlip = () => {
    if (!isFlipped) {
      setIsFlipped(true);
    }
  };

  const handleRating = (lang: 'zh' | 'jp' | 'en', mastery: 0 | 1 | 2) => {
    setLangRatings((prev) => ({ ...prev, [lang]: mastery }));
  };

  const handleNextCard = () => {
    if (!currentWord) return;
    const { zh, jp, en } = langRatings;
    if (zh === undefined || jp === undefined || en === undefined) return;

    updateWordProgress(currentWord.id, 'zh', zh);
    updateWordProgress(currentWord.id, 'jp', jp);
    updateWordProgress(currentWord.id, 'en', en);

    const pointsMap = [0, 3, 5];
    const totalPoints = pointsMap[zh] + pointsMap[jp] + pointsMap[en];
    addPoints(totalPoints, `复习单词: ${currentWord.zh}`);
    setSessionStats((prev) => ({
      totalPoints: prev.totalPoints + totalPoints,
      reviewedCount: prev.reviewedCount + 1,
    }));
    setLangRatings({});

    if (currentIndex < dueWords.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    } else {
      setShowReward(true);
      setTimeout(() => {
        setShowReward(false);
        setShowSummary(true);
      }, 2500);
    }
  };

  const handleSkip = () => {
    setLangRatings({});
    if (currentIndex < dueWords.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsFlipped(false);
    } else {
      setShowSummary(true);
    }
  };

  const handleRestart = () => {
    const words = getWordsForReview();
    setDueWords(words);
    setCurrentIndex(0);
    setIsFlipped(false);
    setShowSummary(false);
    setSessionStats({ totalPoints: 0, reviewedCount: 0 });
    setLangRatings({});
  };

  const allRated = langRatings.zh !== undefined && langRatings.jp !== undefined && langRatings.en !== undefined;

  if (showSummary && dueWords.length === 0) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-8"
          >
            <div className="text-8xl mb-4">🎉</div>
            <h1 className="text-4xl font-heading font-bold text-foreground">今天没有需要复习的单词</h1>
            <p className="text-xl text-muted-foreground">太棒了！所有单词都在掌握中 ✨</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <button
                onClick={() => (window.location.href = '#/learn')}
                className="px-8 py-4 bg-primary text-primary-foreground rounded-2xl text-lg font-semibold hover:scale-105 active:scale-95 transition-all shadow-md"
              >
                🚀 学习新单词
              </button>
              <button
                onClick={() => (window.location.href = '#/')}
                className="px-8 py-4 bg-secondary text-secondary-foreground rounded-2xl text-lg font-semibold hover:scale-105 active:scale-95 transition-all shadow-md"
              >
                🏠 返回首页
              </button>
            </div>
            <div className="mt-8 text-6xl animate-float">🐼🦊🐰</div>
            <p className="text-lg text-muted-foreground">继续保持每天学习的好习惯哦！</p>
          </motion.div>
        </div>
      </Layout>
    );
  }

  if (showSummary) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-12 max-w-4xl">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="space-y-8"
          >
            <div className="text-center">
              <div className="text-7xl mb-4">🌟</div>
              <h1 className="text-4xl font-heading font-bold text-foreground mb-2">复习完成！</h1>
              <p className="text-xl text-muted-foreground">今天复习了 {sessionStats.reviewedCount} 个单词</p>
            </div>
            <div className="bg-card border-2 border-border rounded-3xl p-8 shadow-md space-y-6">
              <h2 className="text-2xl font-heading font-bold text-center">经验值统计</h2>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.3, type: 'spring', stiffness: 200 }}
                className="text-center py-6 bg-accent/10 border border-accent/20 rounded-2xl"
              >
                <div className="text-5xl mb-2">⚡</div>
                <div className="text-3xl font-bold text-accent">
                  {sessionStats.totalPoints > 0 ? '+' : ''}{sessionStats.totalPoints} 积分
                </div>
                <div className="text-sm text-muted-foreground mt-1">本次复习获得</div>
              </motion.div>
              <div className="text-center text-muted-foreground text-sm space-y-1">
                <p>✨ 熟练: +5 积分/语言</p>
                <p>📖 练习: +3 积分/语言</p>
                <p>❓ 加油: +0 积分/语言</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={handleRestart}
                className="px-8 py-4 bg-primary text-primary-foreground rounded-2xl text-lg font-semibold hover:scale-105 active:scale-95 transition-all shadow-md"
              >
                🔄 再次复习
              </button>
              <button
                onClick={() => (window.location.href = '#/')}
                className="px-8 py-4 bg-secondary text-secondary-foreground rounded-2xl text-lg font-semibold hover:scale-105 active:scale-95 transition-all shadow-md"
              >
                🏠 返回首页
              </button>
            </div>
          </motion.div>
        </div>
      </Layout>
    );
  }

  if (!currentWord) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-12 text-center">
          <div className="text-6xl mb-4">🔄</div>
          <p className="text-xl text-muted-foreground">加载中...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <StarBurst show={showReward} />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-xl mx-auto md:max-w-5xl">
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-semibold text-muted-foreground">复习进度</span>
              <span className="text-sm font-bold text-foreground">{currentIndex + 1}/{totalWords}</span>
            </div>
            <PointsBar current={currentIndex + 1} max={totalWords} />
          </div>

          <div className="mb-6 text-center">
            <div className="inline-flex gap-4 text-5xl animate-float mb-3">🐼🦊🐰</div>
            <p className="text-lg text-muted-foreground">点击卡片翻转，然后为每种语言选择掌握程度！</p>
          </div>

          <div className="grid md:grid-cols-[1fr_400px] gap-6">
            <div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentWord.id}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  className="relative"
                  style={{ transformStyle: 'preserve-3d' }}
                >
                  <motion.div
                    animate={{ rotateY: isFlipped ? 180 : 0 }}
                    transition={{ duration: 0.6, type: 'spring', stiffness: 200 }}
                    style={{ transformStyle: 'preserve-3d' }}
                    className="relative"
                  >
                    <div
                      className="absolute inset-0"
                      style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}
                    >
                      <button
                        onClick={handleFlip}
                        disabled={isFlipped}
                        className="w-full bg-card border-2 border-border rounded-3xl p-16 shadow-md hover:shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98] cursor-pointer disabled:cursor-default"
                      >
                        <div className="text-9xl mb-6 text-center animate-bounce-in">{currentWord.emoji}</div>
                        <div className="text-5xl font-bold text-foreground text-center mb-4">{currentWord.zh}</div>
                        {currentWord.pinyin && (
                          <p className="text-xl text-muted-foreground text-center mb-6">{currentWord.pinyin}</p>
                        )}
                        <p className="text-2xl text-muted-foreground mt-6 text-center">点击翻转 🔄</p>
                      </button>
                    </div>

                    <div
                      className="relative"
                      style={{
                        backfaceVisibility: 'hidden',
                        WebkitBackfaceVisibility: 'hidden',
                        transform: 'rotateY(180deg)',
                      }}
                    >
                      <div className="bg-card border-2 border-border rounded-3xl p-8 shadow-md space-y-6">
                        <div className="text-center">
                          <div className="text-8xl mb-4 animate-bounce-in">{currentWord.emoji}</div>
                        </div>

                        <div className="space-y-4">
                          <div className="bg-lang-zh-bg border-2 border-lang-zh-border rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-2xl">🇨🇳</span>
                              <h3 className="text-lg font-heading font-bold text-foreground">中文</h3>
                            </div>
                            <button
                              onClick={() => speakWord(currentWord.zh, 'zh')}
                              className="text-3xl font-bold text-lang-zh hover:scale-105 transition-transform"
                            >
                              {currentWord.zh}
                            </button>
                            {currentWord.pinyin && (
                              <p className="text-sm text-muted-foreground mt-1">{currentWord.pinyin}</p>
                            )}
                            <div className="grid grid-cols-3 gap-2 mt-3">
                              {masteryButtons.map((btn) => (
                                <button
                                  key={btn.level}
                                  onClick={() => handleRating('zh', btn.level)}
                                  className={`p-2 rounded-xl text-center transition-all hover:scale-105 active:scale-95 ${
                                    langRatings.zh === btn.level
                                      ? `${btn.bg} ${btn.text} shadow-md scale-105`
                                      : `bg-background border-2 ${btn.border} text-foreground`
                                  }`}
                                >
                                  <div className="text-2xl mb-1">{btn.emoji}</div>
                                  <div className="text-xs font-bold">{btn.label}</div>
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="bg-lang-jp-bg border-2 border-lang-jp-border rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-2xl">🇯🇵</span>
                              <h3 className="text-lg font-heading font-bold text-foreground">日文</h3>
                            </div>
                            <button
                              onClick={() => speakWord(currentWord.jp, 'jp')}
                              className="text-3xl font-bold text-lang-jp hover:scale-105 transition-transform"
                            >
                              {currentWord.jp}
                            </button>
                            {currentWord.kana && (
                              <p className="text-sm text-muted-foreground mt-1">{currentWord.kana}</p>
                            )}
                            <div className="grid grid-cols-3 gap-2 mt-3">
                              {masteryButtons.map((btn) => (
                                <button
                                  key={btn.level}
                                  onClick={() => handleRating('jp', btn.level)}
                                  className={`p-2 rounded-xl text-center transition-all hover:scale-105 active:scale-95 ${
                                    langRatings.jp === btn.level
                                      ? `${btn.bg} ${btn.text} shadow-md scale-105`
                                      : `bg-background border-2 ${btn.border} text-foreground`
                                  }`}
                                >
                                  <div className="text-2xl mb-1">{btn.emoji}</div>
                                  <div className="text-xs font-bold">{btn.label}</div>
                                </button>
                              ))}
                            </div>
                          </div>

                          <div className="bg-lang-en-bg border-2 border-lang-en-border rounded-2xl p-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-2xl">🇬🇧</span>
                              <h3 className="text-lg font-heading font-bold text-foreground">英文</h3>
                            </div>
                            <button
                              onClick={() => speakWord(currentWord.en, 'en')}
                              className="text-3xl font-bold text-lang-en hover:scale-105 transition-transform"
                            >
                              {currentWord.en}
                            </button>
                            <div className="grid grid-cols-3 gap-2 mt-3">
                              {masteryButtons.map((btn) => (
                                <button
                                  key={btn.level}
                                  onClick={() => handleRating('en', btn.level)}
                                  className={`p-2 rounded-xl text-center transition-all hover:scale-105 active:scale-95 ${
                                    langRatings.en === btn.level
                                      ? `${btn.bg} ${btn.text} shadow-md scale-105`
                                      : `bg-background border-2 ${btn.border} text-foreground`
                                  }`}
                                >
                                  <div className="text-2xl mb-1">{btn.emoji}</div>
                                  <div className="text-xs font-bold">{btn.label}</div>
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              </AnimatePresence>
            </div>

            <div className="hidden md:block">
              <div className="bg-card border-2 border-border rounded-2xl p-6 shadow-md sticky top-4">
                <h3 className="text-xl font-heading font-bold text-foreground mb-4">复习提示</h3>
                <div className="space-y-3 text-sm text-muted-foreground">
                  <p>📌 点击卡片正面翻转查看内容</p>
                  <p>🎯 点击文字可以听发音</p>
                  <p>⭐ 为每种语言选择掌握程度</p>
                  <p>✨ 熟练: +5 积分</p>
                  <p>📖 练习: +3 积分</p>
                  <p>❓ 加油: +0 积分</p>
                </div>
                <div className="mt-6 pt-6 border-t border-border">
                  <div className="text-center">
                    <div className="text-4xl mb-2">⚡</div>
                    <div className="text-2xl font-bold text-accent">{sessionStats.totalPoints}</div>
                    <div className="text-xs text-muted-foreground">本次积分</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {isFlipped && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col sm:flex-row gap-4 justify-center pt-6"
            >
              <button
                onClick={handleSkip}
                className="px-8 py-4 bg-muted text-muted-foreground rounded-2xl text-lg font-semibold hover:scale-105 active:scale-95 transition-all shadow-md"
              >
                跳过 →
              </button>
              <button
                onClick={handleNextCard}
                disabled={!allRated}
                className={`px-12 py-5 rounded-2xl text-xl font-bold transition-all ${
                  allRated
                    ? 'bg-primary text-primary-foreground hover:scale-105 active:scale-95 shadow-md'
                    : 'bg-muted text-muted-foreground cursor-not-allowed opacity-50'
                }`}
              >
                {currentIndex < dueWords.length - 1 ? '下一个 →' : '完成复习 ✓'}
              </button>
            </motion.div>
          )}
          {isFlipped && !allRated && (
            <p className="text-sm text-muted-foreground mt-3 text-center">请为所有三种语言评分后继续</p>
          )}
        </div>
      </div>
    </Layout>
  );
}
